import React from 'react'
import { ExportSquare } from 'iconsax-react'
import { useAppSelector } from '@SonarCX/hooks/redux'

import CommonButton from '../CommonButton'
import ShareTask from '@SonarCX/components/atoms/ShareTask'

import { Routes } from '@SonarCX/Routes'
import { selectDMs } from '@SonarCX/slices/dms'
import useTaskMultiEdit from '../hooks/useTaskMultiEdit'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'

const Share: React.FC = () => {
  const { dms } = useAppSelector(selectDMs)

  const { allUsers } = useUserAPI()
  const {
    api: { messageHandler }
  } = useSocket()
  const { selected, showMessage, suffix } = useTaskMultiEdit()

  const dmsList = React.useMemo(() => Object.values(dms), [dms])
  const [sessions, setSessions] = React.useState<string[]>([])
  const [search, setSearch] = React.useState('')
  const [message, setMessage] = React.useState('')

  const filtered = React.useMemo(() => {
    if (!search) {
      return dmsList
    }

    const lowerSearch = search.toLocaleLowerCase()
    return dmsList.filter(dm =>
      (dm.name ?? '').toLocaleLowerCase().includes(lowerSearch)
    )
  }, [dmsList, search])

  const handleClose = React.useCallback(() => {
    setSessions([])
    setSearch('')
    setMessage('')
  }, [])

  const handleAccept = React.useCallback(async () => {
    const linkText = selected
      .map(id => `${window.location.origin}${Routes.TASKS}/${id}`)
      .join('\n')
    const fullMessage = message ? `${message}\n${linkText}` : linkText

    sessions.forEach(session => {
      const isGroup = !(session in allUsers)
      messageHandler(fullMessage, 'Send', null, session, true, isGroup)
    })

    showMessage(
      `Shared ${selected.length} task${suffix} with ${sessions.length} chat${
        sessions.length === 1 ? '' : 's'
      }`
    )

    handleClose()
  }, [
    selected,
    message,
    sessions,
    allUsers,
    messageHandler,
    showMessage,
    suffix,
    handleClose
  ])

  const updateSelected = React.useCallback(
    (id: string, isSelected: boolean) => {
      if (isSelected) {
        setSessions(old => old.filter(s => s !== id))
      } else {
        setSessions(old => [...old, id])
      }
    },
    []
  )

  return (
    <CommonButton
      title='Share'
      Icon={ExportSquare}
      onAccept={handleAccept}
      onClose={handleClose}
      confirmText='Send'
      confirmDisabled={sessions.length === 0}
      confirmDisabledReason='Select at least one team chat'
    >
      <ShareTask
        search={search}
        setSearch={setSearch}
        filtered={filtered}
        sessions={sessions}
        updateSelected={updateSelected}
        message={message}
        setMessage={setMessage}
      />
    </CommonButton>
  )
}

export default Share
